import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Select, { components as SelectComponents } from 'react-select';
import { useAuth } from '../contexts/AuthContext';
import RequireRole from './RequireRole';

const FiltersContainer = styled.div`
  background: #ecf0f1;
  padding: 1rem 2rem;
  border-bottom: 1px solid #bdc3c7;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
`;

const selectStyles = {
  container: (provided) => ({
    ...provided,
    minWidth: '150px',
    zIndex: 10, // Asegura que el contenedor esté por encima de otros elementos
  }),
  control: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
    borderColor: '#bdc3c7',
    '&:hover': {
      borderColor: '#3498db',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1000, // Asegura que el menú desplegable esté por encima de los controles del mapa
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1000, // Asegura que el portal del menú tenga prioridad
  })
};

// Estilos específicos para el Select de vendedores (campo inteligente)
const vendedorSelectStyles = {
  ...selectStyles,
  container: (provided) => ({
    ...provided,
    minWidth: '260px', // +10% sobre 150px
  }),
  // Oculta las chips seleccionadas para evitar crecimiento horizontal
  multiValue: (base) => ({
    ...base,
    display: 'none',
  }),
};

// ValueContainer personalizado que muestra "N Vendedores" y mantiene el input de búsqueda
const VendedorValueContainer = (props) => {
  const count = props.getValue ? props.getValue().length : 0;
  return (
    <SelectComponents.ValueContainer {...props}>
      {count > 0 && (
        <span style={{ marginLeft: 4, color: '#2c3e50', fontSize: '0.9rem' }}>
          {count} Vendedor{count !== 1 ? 'es' : ''}
        </span>
      )}
      {props.children}
    </SelectComponents.ValueContainer>
  );
};

const Filters = ({ filters, setFilters, vendedores, onReset, onShowDetails, onShowStats, onShowVisitChanges, onShowVendorReassign, polygonActive, onShowPolygonDetails }) => {
  const { isVendedor } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Funciones por defecto para evitar errores
  const safeOnShowVisitChanges = onShowVisitChanges || (() => {});
  const safeOnShowVendorReassign = onShowVendorReassign || (() => {});
  const safeOnShowPolygonDetails = onShowPolygonDetails || (() => {});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const diasSemana = [
    { value: '', label: 'Todos' },
    { value: 'LUNES', label: 'Lunes' },
    { value: 'MARTES', label: 'Martes' },
    { value: 'MIERCOLES', label: 'Miércoles' },
    { value: 'JUEVES', label: 'Jueves' },
    { value: 'VIERNES', label: 'Viernes' },
    { value: 'SABADO', label: 'Sábado' },
    { value: 'DOMINGO', label: 'Domingo' },
  ];

  // Filtrar vendedores según el rol del usuario
  const { isAdmin, isGerente, isSupervisor, isVendedor: isUserVendedor, getCodigosVendedor } = useAuth();
  const codigosVendedor = getCodigosVendedor();
  
  const vendedoresDisponibles = vendedores.filter(vendedor => {
    // Admin puede ver todos los vendedores
    if (isAdmin() || isGerente()) return true;
    
    // Supervisor solo ve vendedores de sus rutas asignadas
    if (isSupervisor()) {
      return codigosVendedor.includes(vendedor.ven_codigo);
    }
    
    // Vendedor solo ve su propio código
    if (isUserVendedor()) {
      return codigosVendedor.includes(vendedor.ven_codigo);
    }
    
    return false;
  });

  const vendedorOptions = [
    ...vendedoresDisponibles.map(vendedor => ({
      value: vendedor.ven_codigo,
      label: vendedor.ven_codigo + ' - ' + vendedor.ven_nombre
    }))
  ];

  const handleFilterChange = (key, value) => {
    // Si cambia la frecuencia, reseteamos la semana de visita
    const updates = { [key]: value };
    if (key === 'frecuencia') {
      updates.semanaVisita = '';
    }
    
    setFilters(prev => ({
      ...prev,
      ...updates
    }));
  };
  
  // Opciones para el filtro de frecuencia
  const frecuenciaOptions = [
    { value: '', label: 'Todas' },
    { value: 'SEMANAL', label: 'Semanal' },
    { value: 'QUINCENAL', label: 'Quincenal' },
    { value: 'MENSUAL', label: 'Mensual' }
  ];
  
  // Opciones para el filtro de semana de visita
  const getSemanaVisitaOptions = () => {
    if (filters.frecuencia === 'QUINCENAL') {
      return [
        { value: '', label: 'Todas' },
        { value: 'SEMANA 1-3', label: 'Semana 1-3' },
        { value: 'SEMANA 2-4', label: 'Semana 2-4' }
      ];
    } else if (filters.frecuencia === 'MENSUAL') {
      return [
        { value: '', label: 'Todas' },
        { value: 'SEMANA 1', label: 'Semana 1' },
        { value: 'SEMANA 2', label: 'Semana 2' },
        { value: 'SEMANA 3', label: 'Semana 3' },
        { value: 'SEMANA 4', label: 'Semana 4' }
      ];
    }
    return [];
  };

  return (
    <FiltersContainer>
      <FilterGroup>
        <SearchInput
          type="text"
          placeholder="🔍 Buscar cliente..."
          value={filters.busqueda}
          onChange={(e) => handleFilterChange('busqueda', e.target.value)}
        />
      </FilterGroup>

      <RequireRole roles={['admin','gerente', 'supervisor']}>
        <FilterGroup>
          <Label>Vendedor:</Label>
          <Select
            styles={vendedorSelectStyles}
            options={vendedorOptions}
            value={filters.vendedor ? (Array.isArray(filters.vendedor) ? 
              filters.vendedor.map(v => vendedorOptions.find(opt => opt.value === v)) : 
              vendedorOptions.find(opt => opt.value === filters.vendedor)) : 
              []}
            onChange={(options) => handleFilterChange('vendedor', options ? 
              (Array.isArray(options) ? options.map(opt => opt.value) : options.value) : 
              [])}
            placeholder="Seleccionar vendedores"
            isClearable={true}
            isMulti={true}
            closeMenuOnSelect={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
            menuShouldBlockScroll={true}
            hideSelectedOptions={false}
            components={{ ValueContainer: VendedorValueContainer }}
          />
        </FilterGroup>
      </RequireRole>

      <FilterGroup>
        <Label>Día:</Label>
        <Select
          styles={selectStyles}
          options={diasSemana}
          value={diasSemana.find(opt => opt.value === filters.dia)}
          onChange={(option) => handleFilterChange('dia', option?.value || '')}
          placeholder="Todos"
          isClearable={false}
        />
      </FilterGroup>
      
      <FilterGroup>
        <Label>Frecuencia:</Label>
        <Select
          styles={selectStyles}
          options={frecuenciaOptions}
          value={frecuenciaOptions.find(opt => opt.value === filters.frecuencia) || null}
          onChange={(option) => handleFilterChange('frecuencia', option?.value || '')}
          placeholder="Todas"
          isClearable={false}
        />
      </FilterGroup>
      
      {filters.frecuencia && filters.frecuencia !== 'SEMANAL' && (
        <FilterGroup>
          <Label>Semana:</Label>
          <Select
            styles={selectStyles}
            options={getSemanaVisitaOptions()}
            value={getSemanaVisitaOptions().find(opt => opt.value === filters.semanaVisita) || null}
            onChange={(option) => handleFilterChange('semanaVisita', option?.value || '')}
            placeholder="Todas"
            isClearable={false}
          />
        </FilterGroup>
      )}
      
      <FilterGroup>
        <button 
          onClick={onReset}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            ':hover': {
              backgroundColor: '#e9ecef'
            }
          }}
        >
          <span>🔄</span>
          <span>Reiniciar</span>
        </button>
      </FilterGroup>
      
      <FilterGroup style={{ marginLeft: 'auto', gap: '0.5rem' }}>
        <button 
          onClick={onShowDetails}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#e9f7fe',
            border: '1px solid #3498db',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#2980b9',
            ':hover': {
              backgroundColor: '#d6eaf8'
            }
          }}
        >
          <span>📊</span>
          <span>Detalle</span>
        </button>

        <button 
          onClick={safeOnShowPolygonDetails}
          disabled={!polygonActive}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#e9f7fe',
            border: '1px solid #3498db',
            borderRadius: '4px',
            cursor: polygonActive ? 'pointer' : 'not-allowed',
            opacity: polygonActive ? 1 : 0.6,
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#2980b9',
            ':hover': {
              backgroundColor: '#d6eaf8'
            }
          }}
          title={polygonActive ? 'Ver detalle de clientes dentro del polígono' : 'Dibuja un polígono para habilitar'}
        >
          <span>🧭</span>
          <span>Detalle Polígono</span>
        </button>
      </FilterGroup>
     
      <FilterGroup>
        <button 
          onClick={onShowStats}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#f0f8ff',
            border: '1px solid #3498db',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#2980b9',
            ':hover': {
              backgroundColor: '#d6eaf8'
            }
          }}
        >
          <span>📈</span>
          <span>Estadísticas</span>
        </button>
      </FilterGroup>

      {/* Dropdown de acciones: Cambios de Visitas y Reasignación de vendedor */}
      <RequireRole roles={['admin','gerente', 'supervisor']}>
        <FilterGroup ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#ffffff',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#2c3e50'
            }}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="Acciones"
          >
            <span>☰</span>
            <span>Acciones</span>
          </button>

          {menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                marginTop: '44px',
                right: 0,
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                padding: '8px',
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minWidth: '260px'
              }}
            >
              <button
                onClick={() => { safeOnShowVisitChanges(); setMenuOpen(false); }}
                disabled={!filters?.vendedor || (Array.isArray(filters.vendedor) ? filters.vendedor.length !== 1 : true)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f8fff0',
                  border: '1px solid #3498db',
                  borderRadius: '4px',
                  cursor: (filters?.vendedor && Array.isArray(filters.vendedor) && filters.vendedor.length === 1) ? 'pointer' : 'not-allowed',
                  opacity: (filters?.vendedor && Array.isArray(filters.vendedor) && filters.vendedor.length === 1) ? 1 : 0.6,
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#2980b9'
                }}
                role="menuitem"
              >
                <span>🗓️</span>
                <span>Cambios de Visitas</span>
              </button>

              <button
                onClick={() => { safeOnShowVendorReassign(); setMenuOpen(false); }}
                disabled={!filters?.vendedor || (Array.isArray(filters.vendedor) ? filters.vendedor.length !== 1 : true)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#fff8f0',
                  border: '1px solid #3498db',
                  borderRadius: '4px',
                  cursor: (filters?.vendedor && Array.isArray(filters.vendedor) && filters.vendedor.length === 1) ? 'pointer' : 'not-allowed',
                  opacity: (filters?.vendedor && Array.isArray(filters.vendedor) && filters.vendedor.length === 1) ? 1 : 0.6,
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#d35400'
                }}
                role="menuitem"
              >
                <span>🔁</span>
                <span>Reasignación de vendedor</span>
              </button>
            </div>
          )}
        </FilterGroup>
      </RequireRole>
    </FiltersContainer>
  );
};

export default Filters;
